/**
 * Lexical JSON utility functions for POI descriptions
 */

interface LexicalTextNode {
  text: string;
  type?: string;
}

interface LexicalElementNode {
  type: string;
  children?: (LexicalTextNode | LexicalElementNode)[];
}

interface LexicalRoot {
  root?: LexicalElementNode;
}

/**
 * Recursively extract text content from Lexical nodes
 */
function extractText(node: unknown): string {
  if (!node || typeof node !== 'object') return '';

  const n = node as Record<string, unknown>;

  // Text node
  if (typeof n.text === 'string') {
    return n.text;
  }

  // Element node with children
  if (Array.isArray(n.children)) {
    return n.children.map(extractText).join('\n');
  }

  return '';
}

/**
 * Extract plain text from Lexical JSON for search indexing
 */
export function lexicalToPlainText(json: unknown): string {
  if (!json || typeof json !== 'object') return '';

  const root = (json as LexicalRoot).root;
  if (!root) return '';

  return extractText(root).trim();
}

/**
 * Check if Lexical JSON has no meaningful content
 */
export function isLexicalEmpty(json: unknown): boolean {
  if (!json) return true;
  if (typeof json !== 'object') return true;

  const text = lexicalToPlainText(json);
  return text.length === 0;
}

/**
 * Wrap plain text in Lexical JSON format
 */
export function plainTextToLexical(text: string): object {
  if (!text || typeof text !== 'string') {
    return {
      root: {
        children: [
          {
            children: [],
            type: 'paragraph',
            direction: null,
            format: '',
            indent: 0,
            version: 1,
          },
        ],
        direction: null,
        format: '',
        indent: 0,
        type: 'root',
        version: 1,
      },
    };
  }

  // Split by newlines and create paragraph nodes
  const lines = text.split('\n');
  const paragraphs = lines.map((line) => ({
    children: line
      ? [
          {
            detail: 0,
            format: 0,
            mode: 'normal',
            style: '',
            text: line,
            type: 'text',
            version: 1,
          },
        ]
      : [],
    type: 'paragraph',
    direction: null,
    format: '',
    indent: 0,
    version: 1,
  }));

  return {
    root: {
      children: paragraphs,
      direction: null,
      format: '',
      indent: 0,
      type: 'root',
      version: 1,
    },
  };
}
